import { useLocation } from "wouter";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AppLayout } from "@/components/app-layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useLanguage } from "@/components/language-provider";
import { getApiUrl } from "@/lib/config";
import { Building2, Download, Settings2, CheckCircle2, Database, Shield, Upload, FileText, Users, ExternalLink, Copy, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function Dynamics365() {
  const [, setLocation] = useLocation();
  const [user, setUser] = useState<any>(null);
  const { language } = useLanguage();
  const { toast } = useToast();
  const pt = language === "pt";
  
  useEffect(() => {
    const stored = localStorage.getItem("user");
    if (!stored) { setLocation("/login"); return; }
    setUser(JSON.parse(stored));
  }, [setLocation]);

  const handleLogout = () => { localStorage.removeItem("token"); localStorage.removeItem("user"); setLocation("/"); };

  const { data: crmRecords, isLoading: crmLoading } = useQuery({
    queryKey: ["/api/documents", "crm"],
    queryFn: async () => {
      const res = await fetch(getApiUrl("/api/documents?source=crm"), { headers: getAuthHeaders() });
      if (!res.ok) return [];
      const data = await res.json();
      return (data.data || data || []).filter((d: any) => d.crmSource);
    },
    enabled: !!user,
  });

  if (!user) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  const isEnterprise = user.isSuperAdmin || user.plan === "enterprise" || user.plan === "custom_enterprise";

  if (!isEnterprise) {
    return (
      <AppLayout user={user} onLogout={handleLogout} plan={user.plan} currentPage="/dynamics365">
        <div className="space-y-6">
          <h1 className="text-2xl font-semibold">Dynamics 365 Integration</h1>
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 text-center">
              <Building2 className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">{pt ? "Recurso Enterprise" : "Enterprise Feature"}</h3>
              <p className="text-muted-foreground mb-4 max-w-md">{pt ? "Integração com Dynamics 365 disponível no plano Enterprise." : "Dynamics 365 integration is available on the Enterprise plan."}</p>
              <Button onClick={() => setLocation("/settings")}>{pt ? "Fazer upgrade" : "Upgrade"}</Button>
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }

  const steps = [
    { title: pt ? "Pré-requisitos" : "Prerequisites", desc: pt ? "Administrador de Sistema no D365 para alterar formulários." : "System Administrator in D365 to edit forms.", icon: Shield },
    { title: pt ? "Baixar solução" : "Download Solution", desc: pt ? "Baixe o .zip da solução gerenciada. Zero configuração no arquivo." : "Download the managed solution .zip. Zero config needed on the file.", icon: Download },
    { title: pt ? "Importar no D365" : "Import in D365", desc: pt ? "Configurações > Soluções > Importar > Upload do .zip." : "Settings > Solutions > Import > Upload the .zip file.", icon: Upload },
    { title: pt ? "Adicionar ao formulário" : "Add to Form", desc: pt ? "Edite o formulário, adicione seção e insira o controle FillSign." : "Edit the form, add a section and insert the FillSign control.", icon: Settings2 },
    { title: pt ? "Configurar propriedades" : "Configure Properties", desc: pt ? "API Key, URL da API e campo para armazenar o ID da assinatura." : "API Key, API URL and field to store the signature request ID.", icon: Database },
    { title: pt ? "Testar" : "Test", desc: pt ? "Abra um registro, envie um documento e verifique o fluxo." : "Open a record, send a document and verify the flow.", icon: CheckCircle2 },
  ];

  const props = [
    { name: "apiKey", desc: pt ? "Chave de API (obrigatório)" : "API key (required)" },
    { name: "apiUrl", desc: pt ? "URL da API" : "API URL", val: getApiUrl("") },
    { name: "fillSignRequestId", desc: pt ? "Campo texto que armazena o ID (bind)" : "Text field that stores the ID (bind)" },
    { name: "language", desc: pt ? "Idioma: pt, en, es" : "Language: pt, en, es" },
    { name: "maxSigners", desc: pt ? "Máximo de assinantes (padrão: 8)" : "Max signers (default: 8)" },
    { name: "enableAiPositioning", desc: pt ? "Posicionamento inteligente por IA" : "AI-powered positioning" },
  ];

  return (
    <AppLayout user={user} onLogout={handleLogout} plan={user.plan} currentPage="/dynamics365">
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold">Dynamics 365 Integration</h1>
          <p className="text-muted-foreground mt-1">{pt ? "Conecte o FillSign ao Microsoft Dynamics 365" : "Connect FillSign with Microsoft Dynamics 365"}</p>
        </div>

        <Tabs defaultValue="wizard" className="space-y-6">
          <TabsList>
            <TabsTrigger value="wizard">{pt ? "Instalação" : "Installation"}</TabsTrigger>
            <TabsTrigger value="config">{pt ? "Configuração" : "Configuration"}</TabsTrigger>
            <TabsTrigger value="records">{pt ? "Registros CRM" : "CRM Records"} {crmRecords?.length ? <Badge variant="secondary" className="ml-1.5 text-xs">{crmRecords.length}</Badge> : null}</TabsTrigger>
          </TabsList>

          {/* Installation */}
          <TabsContent value="wizard" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Building2 className="h-5 w-5" /> {pt ? "Guia de Instalação" : "Installation Guide"}</CardTitle>
                <CardDescription>{pt ? "Siga os passos abaixo. Nenhum código é necessário." : "Follow the steps below. No coding required."}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {steps.map((s, i) => (
                  <div key={i} className="flex gap-4 p-4 rounded-lg border hover:bg-muted/30 transition-colors">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-sm">{i + 1}</div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-0.5">
                        <s.icon className="h-4 w-4 text-primary" />
                        <span className="font-medium text-sm">{s.title}</span>
                      </div>
                      <p className="text-sm text-muted-foreground">{s.desc}</p>
                      {i === 1 && <Button size="sm" className="mt-2" disabled><Download className="h-3.5 w-3.5 mr-1.5" /> {pt ? "Download em breve" : "Download coming soon"}</Button>}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6 space-y-2 text-sm text-muted-foreground">
                <p className="font-medium text-foreground">{pt ? "Observações" : "Notes"}:</p>
                <p>• {pt ? "Solução gerenciada — sem código do lado do cliente." : "Managed solution — no client-side code."}</p>
                <p>• {pt ? "Funciona com qualquer entidade: Quote, Account, Contact, entidades custom." : "Works with any entity: Quote, Account, Contact, custom entities."}</p>
                <p>• {pt ? "Crie um campo texto na entidade para o ID da solicitação." : "Create a text field on the entity for the request ID."}</p>
                <p>• {pt ? "A API Key pode ser um Environment Variable no D365." : "The API Key can be a D365 Environment Variable."}</p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Configuration */}
          <TabsContent value="config" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>{pt ? "Propriedades do Controle" : "Control Properties"}</CardTitle>
                <CardDescription>{pt ? "Configure ao adicionar o controle ao formulário" : "Set when adding the control to the form"}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {props.map((p) => (
                  <div key={p.name} className="flex items-center justify-between p-3 rounded-lg bg-muted/30 gap-3">
                    <div className="min-w-0">
                      <code className="text-sm font-mono font-semibold text-primary">{p.name}</code>
                      <p className="text-xs text-muted-foreground mt-0.5">{p.desc}</p>
                    </div>
                    {p.val && (
                      <Button variant="ghost" size="sm" className="flex-shrink-0 text-xs" onClick={() => { navigator.clipboard.writeText(p.val!); toast({ title: pt ? "Copiado!" : "Copied!" }); }}>
                        <Copy className="h-3 w-3 mr-1" /> {p.val.length > 30 ? p.val.substring(0, 30) + "..." : p.val}
                      </Button>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
                  {pt
                    ? "Funciona com qualquer entidade do Dynamics 365 — padrão ou customizada."
                    : "Works with any Dynamics 365 entity — standard or custom."}
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* CRM Records */}
          <TabsContent value="records">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Users className="h-5 w-5" /> {pt ? "Registros do CRM" : "CRM Records"}</CardTitle>
                <CardDescription>{pt ? "Documentos enviados pelo Dynamics 365" : "Documents sent from Dynamics 365"}</CardDescription>
              </CardHeader>
              <CardContent>
                {crmLoading ? (
                  <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin" /></div>
                ) : crmRecords && crmRecords.length > 0 ? (
                  <div className="space-y-2">
                    {crmRecords.map((doc: any) => (
                      <div key={doc.id} className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/30 transition-colors">
                        <div className="flex items-center gap-3">
                          <FileText className="h-4 w-4 text-primary flex-shrink-0" />
                          <div className="min-w-0">
                            <span className="font-medium text-sm">{doc.name}</span>
                            {doc.crmEntityName && <Badge variant="outline" className="ml-2 text-xs">{doc.crmEntityName}</Badge>}
                            <p className="text-xs text-muted-foreground">{doc.crmRecordName || doc.crmEntityId}{doc.createdAt ? ` • ${new Date(doc.createdAt).toLocaleDateString()}` : ""}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={doc.status === "completed" ? "default" : "secondary"}>{doc.status}</Badge>
                          <Button size="sm" variant="ghost" onClick={() => setLocation(`/documents/${doc.id}`)}><ExternalLink className="h-3.5 w-3.5" /></Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Database className="h-10 w-10 mx-auto mb-3 opacity-50" />
                    <p className="font-medium">{pt ? "Nenhum registro CRM" : "No CRM Records"}</p>
                    <p className="text-sm mt-1">{pt ? "Documentos enviados pelo D365 aparecerão aqui" : "Documents sent from D365 will appear here"}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  );
}
